import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { getShadedCommonground } from "../../utils/helpers";
import Portal from "../ui/portal.js";
import PortalAppBar from "../ui/portalAppBar.js";
import PortalGridContent from "../ui/portalGridContent.js";
import PortalNavDrawer from "../ui/portalNavDrawer.js";
import BotpressAssessments from "../widgets/botpressAssessments.js";
import BotpressConversations from "../widgets/botpressConversations.js";
import BotpressMemberVerifiedAnswers from "../widgets/botpressMemberVerifiedAnswers.js";
import BotpressTestHarness from "../widgets/botpressTestHarness.js";
import BotpressUserFeedback from "../widgets/botpressUserFeedback.js";

const BotpressTestHarnessPortal = props => {
  const { botpressBotODM, testBotODM } = props;
  const theme = useTheme();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Tracks the PROD/TEST bot pairing we've already notified about, so the toast
  // shows once per pairing rather than once per effect run.
  const shownForRef = useRef(null);

  useEffect(() => {
    if (botpressBotODM == null || testBotODM == null) {
      return;
    }
    // Only notify once per unique PROD/TEST bot pairing, so StrictMode's
    // double-invoke (dev) and later reference changes (prod) don't stack
    // duplicate toasts.
    const pairKey = `${botpressBotODM.omniData.botId}:${testBotODM.omniData.botId}`;
    if (shownForRef.current === pairKey) {
      return;
    }
    shownForRef.current = pairKey;

    if (botpressBotODM.omniData.botId === testBotODM.omniData.botId) {
      enqueueSnackbar(
        "The TEST bot is the same as the PROD bot! Be Careful, you are testing in production and this is not recommended.",
        {
          variant: "warning",
        },
      );
    } else {
      enqueueSnackbar(
        "This portal is using your TEST bot. Changes made here will not affect the production environment.",
        {
          variant: "success",
        },
      );
    }
  }, [botpressBotODM, enqueueSnackbar, testBotODM]);

  return (
    <Portal
      displayHeader={false}
      sx={{
        backgroundSize: "cover",
        backgroundColor: getShadedCommonground(theme, 0.4),
      }}
    >
      <PortalAppBar title="Test Candidate" botpressStyle={true} />
      <PortalNavDrawer />
      <PortalGridContent hasNavDrawer>
        {/* The launch pad needs ~400px, so the pair is sized to that rather than
            leaving the harness mostly empty. Kept equal so the two cards stay
            level side by side. */}
        <Grid item size={{ xs: 12, sm: 6, lg: 5 }}>
          {botpressBotODM && <BotpressTestHarness height={"560px"} minHeight={"333px"} />}
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, lg: 7 }}>
          <BotpressAssessments height={"560px"} minHeight={"333px"} />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          <BotpressUserFeedback variant="test" />
        </Grid>
        <Grid item size={{ xs: 12 }}>
          {testBotODM && <BotpressMemberVerifiedAnswers variant="test" minHeight={"333px"} />}
        </Grid>
        <Grid item size={{ xs: 12 }}>
          {testBotODM && <BotpressConversations variant="test" />}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }} />
      </PortalGridContent>
    </Portal>
  );
};

const mapStateToProps = state => ({
  botpressBotODM: state.botpressSession.botpressBotODM,
  testBotODM: state.botpressSession.testBotODM,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressTestHarnessPortal));
